/**
 * 1ファイル分のアップロード処理。
 *
 * 動画と静止画で経路が分かれる。
 *
 *   動画（20MB以上） … Box の分割アップロードセッション → パートを直接 PUT → Worker で確定
 *   静止画（数MB）   … Box の単純アップロード（1リクエスト）
 *
 * 分けるのは Box の仕様上の要請で、**分割アップロードは20MB未満を受け付けない**。
 * どちらの経路でもバイト列は Worker を通らない（CLAUDE.md 原則1）。
 */
import { supabase } from './supabase.ts'
import {
  createMasterSession,
  commitMasterUpload,
  createDirectUploadToken,
  createLightUploadUrl,
  createPreviewFromBox,
  putToSignedUrl,
  enqueueTranscode,
  extractMetadata,
  ApiError,
} from './api.ts'
import { uploadMasterToBox, type UploadProgress } from './chunkedUpload.ts'
import { generateThumbnail, thumbnailFromImage, readVideoMetadata } from './thumbnail.ts'
import { titleFromFileName, type UploadKind } from './uploadQueue.ts'

export type RunnerCallbacks = {
  onProgress: (uploadedBytes: number, percent: number | null) => void
  /** 致命的ではない不具合。アップロード自体は成功している。 */
  onWarning: (message: string) => void
}

export type RunResult = { assetId: string; boxFileId: string; sha1: string | null }

/** Box の単純アップロード（20MB未満向け）。 */
async function uploadSmallFileToBox(args: {
  file: File
  categoryId: string
  signal?: AbortSignal
}): Promise<{ fileId: string; fileName: string; size: number; sha1: string | null }> {
  const token = await createDirectUploadToken({
    categoryId: args.categoryId,
    fileSize: args.file.size,
  })

  const form = new FormData()
  form.append(
    'attributes',
    JSON.stringify({ name: args.file.name, parent: { id: token.folderId } }),
  )
  form.append('file', args.file, args.file.name)

  const res = await fetch(token.uploadUrl, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token.accessToken}` },
    body: form,
    signal: args.signal,
  }).catch((e) => {
    if (e instanceof DOMException && e.name === 'AbortError') throw e
    throw new Error(
      `Box への送信に失敗しました（${e instanceof Error ? e.message : String(e)}）。` +
        '通信環境をご確認ください',
    )
  })

  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    // Box は「許可オリジン未登録」を **プリフライトではなく本リクエストの 403** で返す。
    // そのためブラウザには CORS エラーではなく JSON が見え、原因が分かりにくい。
    // ここで対処まで書いておく（実測で確認済みの挙動）。
    if (detail.includes('cors_origin_not_whitelisted')) {
      throw new Error(
        `Box が ${location.origin} からの送信を拒否しました（許可オリジン未登録）。` +
          'Box Developer Console → アプリ → Configuration → CORS Domains に ' +
          `${location.origin} を追加してください。` +
          '20MB以上の動画は別経路のため影響を受けません',
      )
    }
    throw new Error(`Box への送信が ${res.status} で失敗しました: ${detail.slice(0, 200)}`)
  }

  const body = (await res.json()) as {
    entries?: { id: string; name: string; size: number; sha1?: string }[]
  }
  const entry = body.entries?.[0]
  if (!entry) throw new Error('Box の応答にファイル情報が含まれていません')

  return { fileId: entry.id, fileName: entry.name, size: entry.size, sha1: entry.sha1 ?? null }
}

/**
 * 1ファイルを処理する。
 *
 * 【失敗の扱い】
 * マスターが Box に入った後の失敗（サムネ生成・変換投入・メタデータ抽出）は
 * **警告に留めて成功として返す**。ここでエラーにすると、実体は Box にあるのに
 * 失敗したように見え、やり直しで二重アップロードを招く。
 */
export async function runUpload(args: {
  file: File
  kind: UploadKind
  categoryId: string
  signal?: AbortSignal
  callbacks: RunnerCallbacks
}): Promise<RunResult> {
  const { file, kind, categoryId, signal, callbacks } = args

  // --- 1. サムネイルを先に作る（ブラウザ内） ---
  //
  // アップロードの前に作るのは、失敗しても送信前に気づけるようにするため。
  let thumb: Awaited<ReturnType<typeof generateThumbnail>> | null = null
  try {
    thumb = kind === 'image' ? await thumbnailFromImage(file) : await generateThumbnail(file)
  } catch (e) {
    // ProRes や HEIC など、ブラウザがデコードできない形式では失敗する。
    // 後から Box の画像で作り直せるので、ここでは止めない。
    callbacks.onWarning(
      `サムネイルを生成できませんでした（${e instanceof Error ? e.message : String(e)}）。` +
        'カタログから Box の画像を使って作り直せます',
    )
  }

  try {
    // --- 2. マスターを Box へ ---
    let uploaded: { fileId: string; fileName: string; size: number; sha1: string | null }

    // Box の分割アップロードの下限は **20,000,000 バイト**（10進の20MB）。
    // 実測で確認済み: 19,000,000 は file_size_too_small で 400、20,971,520 は 201。
    // 20 * 1024 * 1024 (=20,971,520) を境界にすると、
    // 20,000,000〜20,971,519 の範囲が単純アップロード側に落ちて
    // 許可オリジン未登録だと失敗する。Box の実際の下限に合わせる。
    const CHUNKED_MIN_BYTES = 20_000_000
    if (file.size >= CHUNKED_MIN_BYTES) {
      const { session, accessToken } = await createMasterSession({
        categoryId,
        fileName: file.name,
        fileSize: file.size,
      })
      const result = await uploadMasterToBox({
        file,
        session,
        accessToken,
        signal,
        onProgress: (p: UploadProgress) =>
          callbacks.onProgress(
            p.uploadedBytes,
            Math.round((p.uploadedBytes / Math.max(p.totalBytes, 1)) * 100),
          ),
        commit: commitMasterUpload,
      })
      uploaded = { ...result, sha1: result.sha1 }
    } else {
      // 単純アップロードは進捗が取れない（fetch は送信量を教えてくれない）。
      // 不定表示にするため percent は null を渡す。
      callbacks.onProgress(0, null)
      uploaded = await uploadSmallFileToBox({ file, categoryId, signal })
      callbacks.onProgress(file.size, 100)
    }

    // --- 3. 素材レコードを作る ---
    const meta = kind === 'video' ? await readVideoMetadata(file).catch(() => null) : null
    const { data: created, error: insertError } = await supabase
      .from('assets')
      .insert({
        category_id: categoryId,
        title: titleFromFileName(file.name),
        kind,
        status: 'processing',
        box_file_id: uploaded.fileId,
        file_name: uploaded.fileName,
        byte_size: uploaded.size,
        sha1: uploaded.sha1,
        content_type: file.type || null,
        duration_ms: meta?.durationMs ?? null,
        width: meta?.width ?? null,
        height: meta?.height ?? null,
      })
      .select('id')
      .single()

    if (insertError || !created) {
      throw new Error(`素材の登録に失敗しました: ${insertError?.message ?? '不明'}`)
    }
    const assetId = created.id as string

    // --- 4. ここから先の失敗は警告に留める（実体は Box に入っている） ---

    if (thumb) {
      try {
        const target = await createLightUploadUrl({
          assetId,
          kind: 'thumbnail',
          contentType: thumb.contentType,
          byteSize: thumb.blob.size,
        })
        await putToSignedUrl(target, thumb.blob)
        await supabase.from('assets').update({ thumbnail_r2_key: target.key }).eq('id', assetId)
      } catch (e) {
        callbacks.onWarning(
          `マスターは保存されましたが、サムネイルの送信に失敗しました（${
            e instanceof Error ? e.message : String(e)
          }）`,
        )
      }
    }

    // メタデータ抽出（撮影日時・機材・撮影地）
    try {
      await extractMetadata(assetId)
    } catch (e) {
      callbacks.onWarning(
        `メタデータを取得できませんでした（${e instanceof ApiError ? e.code : String(e)}）。` +
          'カタログから取り直せます',
      )
    }

    // 写真は詳細表示用のプレビューを作る。
    // 一覧用サムネイル（長辺640px）だけだと、詳細で1枚を吟味できない。
    if (kind === 'image') {
      try {
        await createPreviewFromBox(assetId)
      } catch (e) {
        // Box がまだ representations を作れていない（202 pending）ことがある。
        // 詳細画面から作り直せるので、ここでは警告に留める。
        callbacks.onWarning(
          `詳細表示用プレビューを作成できませんでした（${e instanceof ApiError ? e.code : String(e)}）。` +
            '詳細画面から作成できます',
        )
      }
    }

    // 動画は透かし入りプロキシの生成を依頼する
    if (kind === 'video') {
      try {
        await enqueueTranscode({ assetIds: [assetId] })
      } catch (e) {
        callbacks.onWarning(
          `変換の投入に失敗しました（${e instanceof ApiError ? e.code : String(e)}）。` +
            'カタログから実行できます',
        )
      }
    }

    return { assetId, boxFileId: uploaded.fileId, sha1: uploaded.sha1 }
  } finally {
    if (thumb) URL.revokeObjectURL(thumb.objectUrl)
  }
}
